// EMAStrategyReport.jsx
// EMA5/EMA20 crossover strategy backtest report.
// Run panel (symbol OR watchlist, timeframe, date range) -> results per
// symbol, with per-symbol Excel download and "Open in Charts" (reuses the
// existing markersConfig / V-pattern marker pipeline — no chart changes).

import React, { useState, useEffect } from 'react';
import {
  runEmaStrategy,
  downloadEmaStrategyExcel,
  fetchAllWatchlists,
  fetchStocks,
  createTempWatchlist,
} from '../api';

const TIMEFRAMES = ['5min', '15min', '1H', '1D', '1W'];

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoStr(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function EMAStrategyReport() {
  const [scopeType,   setScopeType]   = useState('symbol');   // 'symbol' | 'watchlist'
  const [symbol,      setSymbol]      = useState('');
  const [watchlistId, setWatchlistId] = useState('');
  const [timeframe,   setTimeframe]   = useState('1D');
  const [fromDate,    setFromDate]    = useState(daysAgoStr(90));
  const [toDate,      setToDate]      = useState(todayStr());
  const [emaPivotN,   setEmaPivotN]   = useState(3);
  const [pricePivotN, setPricePivotN] = useState(3);
  const [dtTolerance] = useState(0.15);   // surfaced later in Feature 3

  const [stocks,     setStocks]     = useState([]);
  const [watchlists, setWatchlists] = useState([]);

  const [running, setRunning] = useState(false);
  const [error,   setError]   = useState('');
  const [data,    setData]    = useState(null);   // scope result

  useEffect(() => {
    fetchStocks().then(rows => setStocks(rows || [])).catch(console.error);
    fetchAllWatchlists().then(rows => setWatchlists(rows || [])).catch(console.error);
  }, []);

  const canRun = scopeType === 'symbol'
    ? !!symbol && !!timeframe && !!fromDate && !!toDate
    : !!watchlistId && !!timeframe && !!fromDate && !!toDate;

  const run = async () => {
    if (!canRun) return;
    setRunning(true);
    setError('');
    setData(null);
    try {
      const payload = {
        scope_type:  scopeType,
        scope_value: scopeType === 'symbol' ? symbol : String(watchlistId),
        timeframe,
        from_date:   fromDate,
        to_date:     toDate,
        ema_pivot_n:      Number(emaPivotN),
        price_pivot_n:    Number(pricePivotN),
        dt_tolerance_pct: Number(dtTolerance),
      };
      const res = await runEmaStrategy(payload);
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const download = async (sym) => {
    try {
      const blob = await downloadEmaStrategyExcel({
        symbol: sym, timeframe, from_date: fromDate, to_date: toDate,
        ema_pivot_n: Number(emaPivotN),
        price_pivot_n: Number(pricePivotN),
        dt_tolerance_pct: Number(dtTolerance),
      });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sym}_${timeframe}_ema_strategy_${fromDate}_${toDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Excel download failed');
    }
  };

  // Open the symbol in a chart tab with entry/exit markers.
  // Mirrors the EMA Crossover report's "Charts" button exactly.
  const openCharts = async (res) => {
    try {
      const tmp = await createTempWatchlist([res.symbol]);
      const layers = {
        trades: res.markers || [],
        ema5:  (res.ema5_pivots  || []).map(p => ({ pattern_time: p.time, kind: p.kind })),
        price: (res.price_pivots || []).map(p => ({ pattern_time: p.time, kind: p.kind })),
      };
      sessionStorage.setItem('markersConfig', JSON.stringify({
        enabled: true,
        layers,
      }));
      const params = new URLSearchParams({
        watchlist:     tmp.watchlist_id,
        showVPatterns: '1',
        vpTimeframe:   timeframe,
      });
      window.open(`${window.location.origin}?${params.toString()}`, '_blank');
    } catch (e) {
      alert('Failed to open charts');
    }
  };

  return (
    <div style={styles.container}>
      {/* ── Run panel ── */}
      <div style={styles.header}>
        <div style={styles.controls}>
          <div style={styles.group}>
            <span style={styles.label}>Scope</span>
            <select style={styles.sel} value={scopeType}
              onChange={e => setScopeType(e.target.value)}>
              <option value="symbol">Single Stock</option>
              <option value="watchlist">Watchlist</option>
            </select>
          </div>

          {scopeType === 'symbol' ? (
            <div style={styles.group}>
              <span style={styles.label}>Stock</span>
              <select style={styles.sel} value={symbol}
                onChange={e => setSymbol(e.target.value)}>
                <option value="">Select…</option>
                {stocks.map(s => (
                  <option key={s.symbol} value={s.symbol}>{s.symbol}</option>
                ))}
              </select>
            </div>
          ) : (
            <div style={styles.group}>
              <span style={styles.label}>Watchlist</span>
              <select style={styles.sel} value={watchlistId}
                onChange={e => setWatchlistId(e.target.value)}>
                <option value="">Select…</option>
                {watchlists.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
          )}

          <div style={styles.group}>
            <span style={styles.label}>Timeframe</span>
            <select style={styles.sel} value={timeframe}
              onChange={e => setTimeframe(e.target.value)}>
              {TIMEFRAMES.map(tf => <option key={tf} value={tf}>{tf}</option>)}
            </select>
          </div>

          <div style={styles.group}>
            <span style={styles.label}>From</span>
            <input type="date" style={styles.dateInput}
              value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </div>
          <div style={styles.group}>
            <span style={styles.label}>To</span>
            <input type="date" style={styles.dateInput}
              value={toDate} onChange={e => setToDate(e.target.value)} />
          </div>

          <div style={styles.group}>
            <span style={styles.label}>EMA5 Pivot N</span>
            <input type="number" min="1" style={{ ...styles.dateInput, width: '64px' }}
              value={emaPivotN} onChange={e => setEmaPivotN(e.target.value)} />
          </div>
          <div style={styles.group}>
            <span style={styles.label}>Price Pivot N</span>
            <input type="number" min="1" style={{ ...styles.dateInput, width: '64px' }}
              value={pricePivotN} onChange={e => setPricePivotN(e.target.value)} />
          </div>

          <button style={{ ...styles.runBtn, opacity: canRun && !running ? 1 : 0.5 }}
            onClick={run} disabled={!canRun || running}>
            {running ? '⏳ Running…' : '▶ Run Backtest'}
          </button>
        </div>
      </div>

      {/* ── Results ── */}
      <div style={styles.content}>
        {error && <div style={styles.error}>{error}</div>}
        {!error && !data && !running && (
          <div style={styles.empty}>
            Choose a stock or watchlist, timeframe and date range, then run.
          </div>
        )}
        {data && (data.results || []).map(res => (
          <ResultCard key={res.symbol} res={res}
            onDownload={() => download(res.symbol)}
            onCharts={() => openCharts(res)} />
        ))}
      </div>
    </div>
  );
}

// ============================================================
// PER-SYMBOL RESULT CARD
// ============================================================
function ResultCard({ res, onDownload, onCharts, onPivots }) {
  const s = res.summary || {};
  const trades = res.trades || [];

  return (
    <div style={styles.card}>
      <div style={styles.cardHead}>
        <span style={styles.sym}>{res.symbol}</span>
        <span style={styles.metrics}>
          Trades {s.total_trades} · Win {s.win_rate}% ·
          Points {s.total_points} · MaxDD {s.max_drawdown} ·
          Against-Struct {s.against_structure}
          {s.open_at_end ? ' · (open at end)' : ''}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button style={styles.smallBtn} onClick={onCharts}>📈 Charts</button>
          <button style={styles.smallBtnPrimary} onClick={onDownload}>⬇ Excel</button>
        </span>
      </div>

      {res.error && <div style={styles.error}>{res.error}</div>}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {['Dir', 'Signal', 'Entry', 'Entry ₹', 'Exit', 'Exit ₹',
                'Reason', 'Points', '% Move', 'Bars', 'Struct'].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trades.map((t, idx) => (
              <tr key={idx} style={
                t.still_open ? styles.openRow
                  : t.structure_ok === false ? styles.againstRow
                  : undefined
              }>
                <td style={styles.td}>
                  <span style={t.direction === 'buy' ? styles.buy : styles.sell}>
                    {t.direction === 'buy' ? 'BUY' : 'SELL'}
                  </span>
                </td>
                <td style={styles.td}>{t.signal_time}</td>
                <td style={styles.td}>{t.entry_time}</td>
                <td style={styles.td}>{t.entry_price}</td>
                <td style={styles.td}>{t.exit_time}</td>
                <td style={styles.td}>{t.exit_price}</td>
                <td style={styles.td}>{t.exit_reason}</td>
                <td style={{ ...styles.td, color: t.points > 0 ? '#2e7d32' : '#c62828' }}>
                  {t.points}
                </td>
                <td style={styles.td}>{t.pct_move}</td>
                <td style={styles.td}>{t.bars_held}</td>
                <td style={styles.td}>
                  {t.structure_ok === true ? '✓'
                    : t.structure_ok === false ? '✗' : '—'}
                </td>
              </tr>
            ))}
            {trades.length === 0 && (
              <tr><td colSpan={11} style={styles.emptyCell}>No trades</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: { display: 'flex', flexDirection: 'column', flex: 1,
    overflow: 'hidden', background: '#f5f5f5' },
  header: { padding: '10px 16px', background: '#fff',
    borderBottom: '1px solid #e0e0e0', flexShrink: 0 },
  controls: { display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' },
  group: { display: 'flex', flexDirection: 'column', gap: '3px' },
  label: { fontSize: '11px', color: '#888', fontWeight: 'bold' },
  sel: { padding: '5px 8px', border: '1px solid #ddd', borderRadius: '4px',
    fontSize: '12px', color: '#444', background: '#fff', minWidth: '120px' },
  dateInput: { padding: '4px 6px', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '12px' },
  runBtn: { padding: '7px 16px', background: '#2962ff', color: '#fff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
    fontSize: '13px', fontWeight: 'bold' },
  content: { flex: 1, overflow: 'auto', padding: '12px 16px' },
  empty: { color: '#888', fontSize: '13px', padding: '24px', textAlign: 'center' },
  error: { color: '#c62828', fontSize: '12px', padding: '8px',
    background: '#fdecea', borderRadius: '4px', marginBottom: '10px' },
  card: { background: '#fff', border: '1px solid #e0e0e0',
    borderRadius: '6px', marginBottom: '14px', overflow: 'hidden' },
  cardHead: { display: 'flex', alignItems: 'center', gap: '12px',
    padding: '8px 12px', borderBottom: '1px solid #eee', flexWrap: 'wrap' },
  sym: { fontWeight: 'bold', fontSize: '14px', color: '#131722' },
  metrics: { fontSize: '12px', color: '#666' },
  smallBtn: { padding: '4px 10px', background: '#f0f4ff', color: '#2962ff',
    border: '1px solid #c5d8fd', borderRadius: '4px', cursor: 'pointer',
    fontSize: '12px' },
  smallBtnPrimary: { padding: '4px 10px', background: '#2962ff', color: '#fff',
    border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
    fontWeight: 'bold' },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: { textAlign: 'left', padding: '6px 8px', background: '#fafafa',
    borderBottom: '1px solid #eee', color: '#555', whiteSpace: 'nowrap' },
  td: { padding: '5px 8px', borderBottom: '1px solid #f2f2f2',
    color: '#444', whiteSpace: 'nowrap' },
  openRow: { background: '#fffbe6' },
  againstRow: { background: '#fdecec' },
  buy: { color: '#2e7d32', fontWeight: 'bold' },
  sell: { color: '#c62828', fontWeight: 'bold' },
  emptyCell: { padding: '16px', textAlign: 'center', color: '#999' },
};

export default EMAStrategyReport;